import React from 'react';
import * as Sentry from '@sentry/react';
import { useRoutes as useRoutesRouter } from 'react-router-dom';
import ErrorPage from '~/components/error_page';
import withPageTitle from '~/hocs/with_page_title';
import withLoadable from '~/hocs/with_loadable';
import lazyWithRetry from '~/utils/lazy_with_retry';
import ProtectedRoute from '~/components/protected_route';
import { PERMISSIONS } from '~/mst/models/permissions/types';
import Layout from '~/layouts/default_layout';
import ContentLayout from '~/layouts/content_layout';
import LogoOnly from '~/layouts/logo_only';

// Lazy load all pages
const PageInitial = lazyWithRetry(() => import('~/pages/initial'));
const PageHome = lazyWithRetry(() => import('~/pages/home'));
const PageDashboardView = lazyWithRetry(() => import('~/pages/dashboard_view'));
const PageDashboards = lazyWithRetry(() => import('~/pages/dashboards'));
const PageCalibrations = lazyWithRetry(() => import('~/pages/calibrations'));
const PageReportEdit = lazyWithRetry(() => import('~/pages/report_edit'));
const PageReportView = lazyWithRetry(() => import('~/pages/report_view'));
const PageReportClone = lazyWithRetry(() => import('~/pages/report_clone'));
const PageReports = lazyWithRetry(() => import('~/pages/reports'));
const PageMap = lazyWithRetry(() => import('~/pages/map'));
const PageProfile = lazyWithRetry(() => import('~/pages/profile'));
const PageDevices = lazyWithRetry(() => import('~/pages/devices'));
const PageDeviceClaim = lazyWithRetry(() => import('~/pages/device_claim'));
const PageDeviceAdmin = lazyWithRetry(() => import('~/pages/device_admin'));
const PageCertificates = lazyWithRetry(() => import('~/pages/certificates'));
const PageCertificateEdit = lazyWithRetry(() => import('~/pages/certificate_edit'));
const PageAlerts = lazyWithRetry(() => import('~/pages/alerts'));
const PageAlertEdit = lazyWithRetry(() => import('~/pages/alert_edit'));
const PageAlertMonitor = lazyWithRetry(() => import('~/pages/alert_monitor'));
const PageUsers = lazyWithRetry(() => import('~/pages/users'));
const PageUserAccess = lazyWithRetry(() => import('~/pages/user_access'));
const PageInvitations = lazyWithRetry(() => import('~/pages/invitations'));
const PageApi = lazyWithRetry(() => import('~/pages/api'));
const PageWebhooks = lazyWithRetry(() => import('~/pages/webhooks'));
const PageArchives = lazyWithRetry(() => import('~/pages/archives'));
const PageOrganizations = lazyWithRetry(() => import('~/pages/organizations'));
const PagePartnerships = lazyWithRetry(() => import('~/pages/partnerships'));
const PageOrganizationEdit = lazyWithRetry(() => import('~/pages/organization_edit'));
const PageDeviceEdit = lazyWithRetry(() => import('~/pages/device_edit'));
const PageDeviceCreate = lazyWithRetry(() => import('~/pages/device_create'));
const PageDeviceMonitor = lazyWithRetry(() => import('~/pages/device_monitor'));
const PagePage_404 = lazyWithRetry(() => import('~/pages/page_404'));
const PageNoesis = lazyWithRetry(() => import('~/pages/noesis'));

// withPage now includes withLoadable for Suspense support
const withPage = (page: React.ComponentType) => withPageTitle(Sentry.withErrorBoundary(withLoadable(page), { fallback: <ErrorPage /> }));

const Initial = withPage(PageInitial);
const Home = withPage(PageHome);
const Dashboard = withPage(PageDashboardView);
const Dashboards = withPage(PageDashboards);
const Calibrations = withPage(PageCalibrations);
const ReportEdit = withPage(PageReportEdit);
const ReportView = withPage(PageReportView);
const ReportClone = withPage(PageReportClone);
const Reports = withPage(PageReports);
const Map = withPage(PageMap);
const UserProfile = withPage(PageProfile);
const Devices = withPage(PageDevices);
const DevicesClaim = withPage(PageDeviceClaim);
const DevicesAdmin = withPage(PageDeviceAdmin);
const Certificates = withPage(PageCertificates);
const CertificateEdit = withPage(PageCertificateEdit);
const Alerts = withPage(PageAlerts);
const AlertEdit = withPage(PageAlertEdit);
const AlertMonitor = withPage(PageAlertMonitor);
const Users = withPage(PageUsers);
const UserAccess = withPage(PageUserAccess);
const Invitations = withPage(PageInvitations);
const Api = withPage(PageApi);
const Webhooks = withPage(PageWebhooks);
const Archives = withPage(PageArchives);
const Organizations = withPage(PageOrganizations);
const Partnerships = withPage(PagePartnerships);
const OrganizationsEdit = withPage(PageOrganizationEdit);
const DeviceEdit = withPage(PageDeviceEdit);
const DeviceCreate = withPage(PageDeviceCreate);
const DeviceMonitor = withPage(PageDeviceMonitor);
const Noesis = withPage(PageNoesis);
const Page404 = withPage(PagePage_404);

export const getSlugPath = (path: string) => `/:slug${path}`;

export const routes = [
  {
    path: '/',
    children: [
      {
        element: <Layout />,
        children: [
          {
            element: <ContentLayout $noPadding />,
            children: [
              {
                path: '',
                element: <Initial />
              },
              {
                path: getSlugPath('/home'),
                element: (
                  <ProtectedRoute>
                    <Home title="home.title" />
                  </ProtectedRoute>
                )
              },
              {
                path: getSlugPath('/dashboards'),
                children: [
                  {
                    path: '',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ReadDashboards}>
                        <Dashboards title="dashboards.list.title" />
                      </ProtectedRoute>
                    )
                  },
                  {
                    path: 'noesis',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ReadDashboards}>
                        <Dashboards title="dashboards.list.title" />
                      </ProtectedRoute>
                    )
                  },
                  {
                    path: 'noesis/:threadId',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ReadDashboards}>
                        <Dashboards title="dashboards.list.title" />
                      </ProtectedRoute>
                    )
                  },
                  {
                    path: getSlugPath('/dashboards/:id'),
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ReadDashboards}>
                        <Dashboard title="dashboard.title" />
                      </ProtectedRoute>
                    )
                  }
                ]
              },
              {
                path: getSlugPath('/devices'),
                children: [
                  {
                    path: '',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ReadDevices}>
                        <Devices title="devices_page.title" />
                      </ProtectedRoute>
                    )
                  },
                  {
                    path: 'claim',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ManageDevices}>
                        <DevicesClaim title="devices_page.title" />
                      </ProtectedRoute>
                    )
                  },
                  {
                    path: ':id/monitor',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ReadDevices}>
                        <DeviceMonitor title="device_monitor_page.title" />
                      </ProtectedRoute>
                    )
                  },
                  {
                    path: ':id/create',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ManageDevices}>
                        <DeviceCreate title="device_create_configuration_page.title" />
                      </ProtectedRoute>
                    )
                  },
                  {
                    path: ':id/admin',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ManageDevices}>
                        <DevicesAdmin title="device_admin_configuration_page.title" />
                      </ProtectedRoute>
                    )
                  },
                  {
                    path: ':id/edit',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ManageDevices}>
                        <DeviceEdit title="device_configuration_page.title" />
                      </ProtectedRoute>
                    )
                  }
                ]
              },
              {
                path: getSlugPath('/certificates'),
                children: [
                  {
                    path: '',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ReadDevices}>
                        <Certificates title="certificates.title" />
                      </ProtectedRoute>
                    )
                  },
                  {
                    path: 'create',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ManageDevices}>
                        <CertificateEdit title="certificates.new" />
                      </ProtectedRoute>
                    )
                  },
                  {
                    path: ':id',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ManageDevices}>
                        <CertificateEdit title="certificates.edit" />
                      </ProtectedRoute>
                    )
                  }
                ]
              },
              {
                path: getSlugPath('/reports'),
                children: [
                  {
                    path: '',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ReadReports}>
                        <Reports title="reports.title" />
                      </ProtectedRoute>
                    )
                  },
                  {
                    path: 'create',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ManageReports}>
                        <ReportEdit title="reports.new_title" />
                      </ProtectedRoute>
                    )
                  },
                  {
                    path: 'noesis',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ReadReports}>
                        <Reports title="reports.title" />
                      </ProtectedRoute>
                    )
                  },
                  {
                    path: 'noesis/:threadId',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ReadReports}>
                        <Reports title="reports.title" />
                      </ProtectedRoute>
                    )
                  },
                  {
                    path: ':id',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ManageReports}>
                        <ReportEdit title="reports.edit_title" />
                      </ProtectedRoute>
                    )
                  },
                  {
                    path: ':id/clone',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ManageReports}>
                        <ReportClone title="reports.clone_title" />
                      </ProtectedRoute>
                    )
                  },
                  {
                    path: ':id/view',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ReadReports}>
                        <ReportView title="reports.view_title" />
                      </ProtectedRoute>
                    )
                  },
                  {
                    path: ':id/view/noesis',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ReadReports}>
                        <ReportView title="reports.view_title" />
                      </ProtectedRoute>
                    )
                  },
                  {
                    path: ':id/view/noesis/:threadId',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ReadReports}>
                        <ReportView title="reports.view_title" />
                      </ProtectedRoute>
                    )
                  },
                  {
                    path: ':id/view/:range',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ReadReports}>
                        <ReportView title="reports.view_title" />
                      </ProtectedRoute>
                    )
                  }
                ]
              },
              {
                path: getSlugPath('/alerts'),
                children: [
                  {
                    path: '',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ReadDevices}>
                        <Alerts title="alerts.title" />
                      </ProtectedRoute>
                    )
                  },
                  {
                    path: 'create',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ManageDevices}>
                        <AlertEdit title="alerts.new" />
                      </ProtectedRoute>
                    )
                  },
                  {
                    path: 'noesis',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ReadDevices}>
                        <Alerts title="alerts.title" />
                      </ProtectedRoute>
                    )
                  },
                  {
                    path: 'noesis/:threadId',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ReadDevices}>
                        <Alerts title="alerts.title" />
                      </ProtectedRoute>
                    )
                  },
                  {
                    path: ':id/monitor',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ReadDevices}>
                        <AlertMonitor title="alerts.monitor_page_title" />
                      </ProtectedRoute>
                    )
                  },
                  {
                    path: ':id',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ManageDevices}>
                        <AlertEdit title="alerts.config_page_title" />
                      </ProtectedRoute>
                    )
                  }
                ]
              },
              {
                path: getSlugPath('/calibrations'),
                children: [
                  {
                    path: '',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ManageDevices}>
                        <Calibrations title="calibrations.title" />
                      </ProtectedRoute>
                    )
                  }
                ]
              },
              {
                path: getSlugPath('/noesis'),
                children: [
                  {
                    path: '',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ReadDevices}>
                        <Noesis title="analytics.title" />
                      </ProtectedRoute>
                    )
                  },
                  {
                    path: ':threadId',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ReadDevices}>
                        <Noesis title="analytics.title" />
                      </ProtectedRoute>
                    )
                  }
                ]
              },
              {
                path: getSlugPath('/profile'),
                element: <UserProfile title="user_profile.title" />
              },
              {
                path: getSlugPath('/users'),
                children: [
                  {
                    path: '',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ReadUsers}>
                        <Users title="users.title" />
                      </ProtectedRoute>
                    )
                  },
                  {
                    path: ':id/access',
                    element: (
                      <ProtectedRoute permission={PERMISSIONS.ManageUsers}>
                        <UserAccess title="users.access_form.title" />
                      </ProtectedRoute>
                    )
                  }
                ]
              },
              {
                path: getSlugPath('/invitations'),
                element: (
                  <ProtectedRoute permission={PERMISSIONS.ReadUsers}>
                    <Invitations title="user_invitations.title" />
                  </ProtectedRoute>
                )
              },
              {
                path: getSlugPath('/api'),
                element: (
                  <ProtectedRoute permission={PERMISSIONS.ManageDevices}>
                    <Api title="api.title" />
                  </ProtectedRoute>
                )
              },
              {
                path: getSlugPath('/webhooks'),
                element: (
                  <ProtectedRoute permission={PERMISSIONS.ManageDevices}>
                    <Webhooks title="webhooks.title" />
                  </ProtectedRoute>
                )
              },
              {
                path: getSlugPath('/archives'),
                element: (
                  <ProtectedRoute permission={PERMISSIONS.ReadDevices}>
                    <Archives title="archives.title" />
                  </ProtectedRoute>
                )
              },
              {
                path: getSlugPath('/partnerships'),
                element: (
                  <ProtectedRoute permission={PERMISSIONS.ReadOrganizations}>
                    <Partnerships title="partnerships.title" />
                  </ProtectedRoute>
                )
              },
              {
                path: getSlugPath('/organizations'),
                children: [
                  {
                    path: '',
                    element: <Organizations title="organizations.title" />
                  },
                  {
                    path: 'new',
                    element: <OrganizationsEdit title="organizations.new" />
                  },
                  {
                    path: ':id',
                    element: <OrganizationsEdit title="organizations.edit" />
                  }
                ]
              }
            ]
          },
          {
            element: <ContentLayout $noPadding />,
            children: [
              {
                path: getSlugPath('/map'),
                element: (
                  <ProtectedRoute permission={PERMISSIONS.ReadDevices}>
                    <Map title="map.title" />
                  </ProtectedRoute>
                )
              }
            ]
          }
        ]
      }
    ]
  },
  {
    path: '*',
    element: <LogoOnly />,
    children: [
      {
        path: '*',
        element: <Page404 title="Page Not Found" />
      }
    ]
  }
];

const useRoutes = Sentry.wrapUseRoutesV7(useRoutesRouter);

export default () => useRoutes(routes);
